import type { IChat, IMessage } from '@/modules/chat-matrix-fast/types/IChat'
import { useChatMessageToast } from '@/modules/chat-matrix-fast/composables/useChatMessageToast'
import { getChatNotificationsEnabled } from '@/modules/chat-matrix-fast/utils/chatNotifications'
import {
  bindChatSystemNotificationPermissionGesture,
  shouldUseChatSystemNotification,
  showChatSystemNotification,
} from '@/modules/chat-matrix-fast/utils/chatSystemNotification'
import { findViewAvatarByMatrixUserId } from '@/modules/chat-matrix-fast/stores/chatStoreSync'
import { normalizeAvatarUrl, formatListMessagePreview } from '@/modules/chat-matrix-fast/utils'
import {
  getMatrixClient,
  isMatrixClientActive,
} from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import { toAuthenticatedMatrixMediaUrl } from '@/modules/chat-matrix-fast/services/matrixMediaUrl'
import { resolveMatrixSenderProfile } from '@/modules/chat-matrix-fast/services/matrixProfileCache'

function resolveSenderMxid(message: IMessage): string {
  return String(message.author_id || message.created_by?.id || message.author || '').trim()
}

function resolveSenderName(message: IMessage, chat?: IChat | null): string {
  const createdBy = message.created_by

  if (createdBy?.last_name || createdBy?.first_name) {
    return `${createdBy.last_name ?? ''} ${createdBy.first_name ?? ''}`.trim()
  }

  const dialogMember = chat?.dialog_member
  if (dialogMember?.last_name || dialogMember?.first_name) {
    return `${dialogMember.last_name ?? ''} ${dialogMember.first_name ?? ''}`.trim()
  }

  return (
    createdBy?.full_name
    || message.author_name
    || message.name
    || createdBy?.user_name
    || createdBy?.username
    || 'Новое сообщение'
  )
}

function resolveAvatarFromChat(chat: IChat | null | undefined, mxid: string): string | null {
  if (!chat) {
    return null
  }

  const dialogAvatar = normalizeAvatarUrl(chat.dialog_member?.avatar)
  if (dialogAvatar) {
    const dialogMxid = String(chat.dialog_member?.matrix_user_id || chat.dialog_member?.id || '').trim()
    if (!dialogMxid || !mxid || dialogMxid === mxid) {
      return dialogAvatar
    }
  }

  const member = chat.members?.find(item => {
    const candidate = String(item.user_uuid || item.user || item.user_id || '').trim()
    return candidate && candidate === mxid
  })

  return normalizeAvatarUrl(member?.avatar || member?.photo) || null
}

function resolveAvatarFromMatrixRoom(roomId: string | undefined, mxid: string): string | null {
  if (!roomId || !mxid.startsWith('@') || !isMatrixClientActive()) {
    return null
  }

  try {
    const matrixClient = getMatrixClient()
    const mxcAvatar = matrixClient.getRoom(roomId)?.getMember(mxid)?.getMxcAvatarUrl?.()
    if (!mxcAvatar) {
      return null
    }

    return toAuthenticatedMatrixMediaUrl(matrixClient, mxcAvatar, 96, 96, 'crop') || null
  } catch {
    return null
  }
}

function resolveSenderAvatarSync(
  message: IMessage,
  options?: { roomId?: string; chat?: IChat | null },
): string | null {
  const mxid = resolveSenderMxid(message)

  if (mxid.startsWith('@')) {
    const viewAvatar = findViewAvatarByMatrixUserId(mxid)
    if (viewAvatar) {
      return viewAvatar
    }
  }

  const fromMessage = normalizeAvatarUrl(message.avatar || message.created_by?.avatar)
  if (fromMessage) {
    return fromMessage
  }

  const fromChat = resolveAvatarFromChat(options?.chat, mxid)
  if (fromChat) {
    return fromChat
  }

  return resolveAvatarFromMatrixRoom(options?.roomId, mxid)
}

function resolveMessagePreview(message: IMessage): string {
  return formatListMessagePreview(message.content, message.attachments) || 'Новое сообщение'
}

async function enrichToastAvatar(
  chatId: number | string,
  message: IMessage,
  roomId?: string,
): Promise<void> {
  const mxid = resolveSenderMxid(message)
  if (!mxid.startsWith('@') || !isMatrixClientActive()) {
    return
  }

  try {
    const matrixClient = getMatrixClient()
    const room = roomId ? matrixClient.getRoom(roomId) : null
    const profile = await resolveMatrixSenderProfile(matrixClient, mxid, room)
    if (!profile.avatarUrl) {
      return
    }

    const { toasts, patchToast } = useChatMessageToast()
    const target = [...toasts.value].reverse().find(item => item.chatId === chatId)
    if (!target) {
      return
    }

    patchToast(chatId, {
      avatar: profile.avatarUrl,
      senderName: target.senderName === 'Новое сообщение' || target.senderName.startsWith('@')
        ? profile.displayName
        : target.senderName,
    })
  } catch {
    // ignore
  }
}

/** In-app toast в ленту; при скрытой вкладке — ещё OS-уведомление. */
export function showChatNewMessageToast(
  chatId: number | string | null | undefined,
  message: IMessage,
  options?: { roomId?: string; chat?: IChat | null },
): void {
  if (!chatId || !getChatNotificationsEnabled(chatId)) {
    return
  }

  bindChatSystemNotificationPermissionGesture()

  const avatar = resolveSenderAvatarSync(message, options)
  const payload = {
    chatId,
    senderName: resolveSenderName(message, options?.chat),
    messagePreview: resolveMessagePreview(message),
    avatar,
    roomId: options?.roomId,
  }

  if (shouldUseChatSystemNotification()) {
    void showChatSystemNotification(payload)
  }

  const { show } = useChatMessageToast()
  show(payload)

  if (!avatar) {
    void enrichToastAvatar(chatId, message, options?.roomId)
  }
}
